import { useMemo } from "react";

import { EmptystateVariablesColored } from "components/icons/generated";
import PageWrapper from "components/PageWrapper";
import Button from "ds/components/Button";
import CollapsibleSection from "ds/components/CollapsibleSection";
import Counter from "ds/components/Counter";
import { useActiveDrawerId } from "ds/components/DrawerNew/useActiveDrawerId";
import EmptyState from "ds/components/EmptyState";
import PageInfoHeader from "ds/components/PageInfoHeader";
import TableSimple from "ds/components/TableSimple";
import Tooltip from "ds/components/Tooltip";
import useTypedContext from "hooks/useTypedContext";
import { StackContextAttachment } from "types/generated";

import { showEnvVariableFormDrawer } from "../components/EnvVariablesDrawer/EnvVariablesFormDrawer";
import { showMountedFilesFormDrawer } from "../components/MountedFileDrawer/MountedFilesFormDrawer";
import {
  ATTACHED_CONTEXTS_CELLS,
  ENV_VARS_CELLS,
  SPACELIFT_VARS_CELLS,
} from "../components/Table/Cells";
import tableStyles from "../components/Table/styles.module.css";
import EnvironmentMountedFilesTable from "../components/Table/MountedFilesTable";
import {
  useEnvironmentContextsTableStore,
  useEnvironmentMountedFilesTableStore,
  useEnvironmentSpaceliftVariablesTableStore,
  useEnvironmentVariablesTableStore,
} from "../components/Table/stores";
import { EnvironmentContext } from "../EnvironmentContext";
import { EnvironmentVariable } from "../types";

type EnvironmentPageProps = {
  localVariables: EnvironmentVariable[];
  mountedFiles: EnvironmentVariable[];
  spaceliftVariables: EnvironmentVariable[];
  attachedContexts: StackContextAttachment[];
};

const EnvironmentPage = ({
  localVariables,
  mountedFiles,
  spaceliftVariables,
  attachedContexts,
}: EnvironmentPageProps) => {
  const { canCreateAndEditVariables, onCreate } = useTypedContext(EnvironmentContext);
  const activeRowId = useActiveDrawerId();

  const isAddVariableVisible =
    canCreateAndEditVariables.result || !!canCreateAndEditVariables.message;

  const existingNames = useMemo(() => localVariables.map((v) => v.id), [localVariables]);

  return (
    <>
      <PageInfoHeader title="Environment" />
      <PageWrapper margin="0" padding="0" gap="x-large">
        <CollapsibleSection
          ariaLevel={3}
          title="Environment variables"
          initialIsCollapsed={false}
          titleSuffix={<Counter count={localVariables.length} />}
          ariaLabel="Environment variables"
          fullbleed
          headerSuffix={
            isAddVariableVisible ? (
              <Tooltip
                textAlign="center"
                active={!!canCreateAndEditVariables.message}
                on={(tooltipProps) => (
                  <Button
                    {...tooltipProps}
                    variant="secondary"
                    disabled={!canCreateAndEditVariables.result}
                    onPress={() =>
                      showEnvVariableFormDrawer({
                        onSubmit: onCreate,
                        existingNames,
                      })
                    }
                  >
                    Add variable
                  </Button>
                )}
              >
                {canCreateAndEditVariables.message}
              </Tooltip>
            ) : undefined
          }
        >
          <TableSimple
            data={localVariables}
            store={useEnvironmentVariablesTableStore}
            cellPerColumn={ENV_VARS_CELLS}
            ariaLabel="Environment variables"
            activeRowId={activeRowId}
            hasPagination={false}
            wrapperClassName={tableStyles.contentHeight}
            emptyState={
              <EmptyState
                padding="x-large 0"
                icon={EmptystateVariablesColored}
                title="You do not have any variables yet"
                caption="Environment variables are represented as a key-value mapping available to all processes running in a given environment. Click on the button above to create a new variable."
              />
            }
          />
        </CollapsibleSection>
        <CollapsibleSection
          ariaLevel={3}
          title="Mounted files"
          titleSuffix={<Counter count={mountedFiles.length} />}
          ariaLabel="Mounted files"
          fullbleed
          headerSuffix={
            isAddVariableVisible ? (
              <Tooltip
                textAlign="center"
                active={!!canCreateAndEditVariables.message}
                on={(tooltipProps) => (
                  <Button
                    {...tooltipProps}
                    variant="secondary"
                    disabled={!canCreateAndEditVariables.result}
                    onPress={() =>
                      showMountedFilesFormDrawer({
                        onSubmit: onCreate,
                      })
                    }
                  >
                    Add file
                  </Button>
                )}
              >
                {canCreateAndEditVariables.message}
              </Tooltip>
            ) : undefined
          }
        >
          <EnvironmentMountedFilesTable
            data={mountedFiles}
            store={useEnvironmentMountedFilesTableStore}
            ariaLabel="Mounted files"
            activeRowId={activeRowId}
            emptyState={
              <EmptyState
                padding="x-large 0"
                icon={EmptystateVariablesColored}
                title="There are no mounted files for this environment"
                caption="Mounted files are represented as a key-value mapping available to all processes running in a given environment."
              />
            }
          />
        </CollapsibleSection>
        <CollapsibleSection
          ariaLevel={3}
          title="Spacelift environment"
          titleSuffix={<Counter count={spaceliftVariables.length} />}
          ariaLabel="Spacelift environment"
          fullbleed
        >
          <TableSimple
            data={spaceliftVariables}
            store={useEnvironmentSpaceliftVariablesTableStore}
            cellPerColumn={SPACELIFT_VARS_CELLS}
            ariaLabel="Spacelift environment"
            hasPagination={false}
            wrapperClassName={tableStyles.contentHeight}
            emptyState={
              <EmptyState
                padding="x-large 0"
                icon={EmptystateVariablesColored}
                title="There are no Spacelift variables for this environment"
                caption="Environment variables are represented as a key-value mapping available to all processes running in a given environment."
              />
            }
          />
        </CollapsibleSection>

        {!!attachedContexts.length && (
          <CollapsibleSection
            ariaLevel={3}
            title="Attached contexts"
            titleSuffix={<Counter count={attachedContexts.length} />}
            fullbleed
            ariaLabel="Attached contexts"
          >
            <TableSimple
              data={attachedContexts}
              store={useEnvironmentContextsTableStore}
              cellPerColumn={ATTACHED_CONTEXTS_CELLS}
              ariaLabel="Attached contexts"
              hasPagination={false}
              wrapperClassName={tableStyles.contentHeight}
            />
          </CollapsibleSection>
        )}
      </PageWrapper>
    </>
  );
};

export default EnvironmentPage;
